import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  collection,
  increment,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { addPoints, POINT_VALUES } from "./pointsService";

/**
 * Generate a unique referral code for a user
 * @param {string} address - Wallet address
 * @returns {string} Referral code
 */
export const generateReferralCode = (address) => {
  // Use first 6 and last 4 characters of address + random suffix
  const normalized = address.toLowerCase();
  const prefix = normalized.slice(2, 8); // Skip '0x' and take next 6 chars
  const suffix = normalized.slice(-4);
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}${suffix}${random}`.toUpperCase();
};

/**
 * Get or create referral code for user
 * @param {string} address - Wallet address
 * @returns {Promise<string>} Referral code
 */
export const getOrCreateReferralCode = async (address) => {
  try {
    if (!address) {
      throw new Error("Wallet address is required");
    }

    const normalizedAddress = address.toLowerCase();
    const userRef = doc(db, "users", normalizedAddress);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();
      if (userData.referralCode) {
        return userData.referralCode;
      }
    }

    // Generate new referral code
    let referralCode = generateReferralCode(normalizedAddress);
    let attempts = 0;
    const maxAttempts = 10;

    // Ensure code is unique
    while (attempts < maxAttempts) {
      const codeRef = doc(db, "referralCodes", referralCode);
      const codeDoc = await getDoc(codeRef);

      if (!codeDoc.exists()) {
        // Code is unique, save it
        await setDoc(codeRef, {
          code: referralCode,
          userAddress: normalizedAddress,
          createdAt: serverTimestamp(),
        });

        // Update user document
        if (userDoc.exists()) {
          await updateDoc(userRef, {
            referralCode: referralCode,
            updatedAt: serverTimestamp(),
          });
        } else {
          await setDoc(userRef, {
            address: normalizedAddress,
            referralCode: referralCode,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }

        return referralCode;
      }

      // Code exists, generate new one
      referralCode = generateReferralCode(normalizedAddress + attempts);
      attempts++;
    }

    throw new Error("Failed to generate unique referral code");
  } catch (error) {
    console.error("Error getting referral code:", error);
    throw error;
  }
};

/**
 * Get referral code by code string
 * @param {string} code - Referral code
 * @returns {Promise<object|null>} Referral code data or null
 */
export const getReferralCode = async (code) => {
  try {
    if (!code) return null;

    const codeRef = doc(db, "referralCodes", code.toUpperCase());
    const codeDoc = await getDoc(codeRef);

    if (codeDoc.exists()) {
      return { id: codeDoc.id, ...codeDoc.data() };
    }

    return null;
  } catch (error) {
    console.error("Error getting referral code:", error);
    return null;
  }
};

/**
 * Process referral when a new user signs up
 * @param {string} newUserAddress - New user's wallet address
 * @param {string} referralCode - Referral code used
 * @returns {Promise<object>} Result object
 */
export const processReferral = async (newUserAddress, referralCode) => {
  try {
    if (!newUserAddress || !referralCode) {
      return { success: false, error: "Missing required parameters" };
    }

    const normalizedNewUser = newUserAddress.toLowerCase();
    const codeData = await getReferralCode(referralCode);

    if (!codeData) {
      return { success: false, error: "Invalid referral code" };
    }

    const referrerAddress = codeData.userAddress;

    // Don't allow self-referral
    if (referrerAddress === normalizedNewUser) {
      return { success: false, error: "Cannot refer yourself" };
    }

    // Check if this user has already used a referral code
    const newUserRef = doc(db, "users", normalizedNewUser);
    const newUserDoc = await getDoc(newUserRef);

    if (newUserDoc.exists()) {
      const newUserData = newUserDoc.data();
      if (newUserData.referredBy) {
        return { success: false, error: "User has already used a referral code" };
      }
    }

    // Record the referral
    const referralData = {
      referrerAddress: referrerAddress,
      referredAddress: normalizedNewUser,
      referralCode: referralCode.toUpperCase(),
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    };

    await setDoc(doc(db, "referrals", `${referrerAddress}_${normalizedNewUser}`), referralData);

    // Update new user's document
    if (newUserDoc.exists()) {
      await updateDoc(newUserRef, {
        referredBy: referrerAddress,
        referralCodeUsed: referralCode.toUpperCase(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(newUserRef, {
        address: normalizedNewUser,
        referredBy: referrerAddress,
        referralCodeUsed: referralCode.toUpperCase(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    // Award points to referrer
    await addPoints(
      referrerAddress,
      POINT_VALUES.REFERRAL_SIGNUP,
      "referral_signup",
      {
        referredAddress: normalizedNewUser,
        referralCode: referralCode.toUpperCase(),
      }
    );

    // Award bonus points to new user
    await addPoints(
      normalizedNewUser,
      POINT_VALUES.REFERRAL_BONUS,
      "referral_bonus",
      {
        referrerAddress: referrerAddress,
        referralCode: referralCode.toUpperCase(),
      }
    );

    // Update referral code stats
    const codeRef = doc(db, "referralCodes", referralCode.toUpperCase());
    await updateDoc(codeRef, {
      totalUses: increment(1),
      lastUsedAt: serverTimestamp(),
    });

    return {
      success: true,
      referrerAddress,
      pointsAwarded: {
        referrer: POINT_VALUES.REFERRAL_SIGNUP,
        newUser: POINT_VALUES.REFERRAL_BONUS,
      },
    };
  } catch (error) {
    console.error("Error processing referral:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get referral statistics for a user
 * @param {string} address - Wallet address
 * @returns {Promise<object>} Referral stats
 */
export const getReferralStats = async (address) => {
  try {
    if (!address) {
      return {
        totalReferrals: 0,
        referralCode: null,
        referrals: [],
      };
    }

    const normalizedAddress = address.toLowerCase();
    const referralCode = await getOrCreateReferralCode(normalizedAddress);

    // Get all referrals made by this user
    const q = query(
      collection(db, "referrals"),
      where("referrerAddress", "==", normalizedAddress)
    );

    const querySnapshot = await getDocs(q);
    const referrals = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return {
      totalReferrals: referrals.length,
      referralCode,
      referrals: referrals.sort((a, b) => {
        const timeA = a.timestamp?.toDate?.()?.getTime() || 
                      (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const timeB = b.timestamp?.toDate?.()?.getTime() || 
                      (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return timeB - timeA;
      }),
    };
  } catch (error) {
    console.error("Error getting referral stats:", error);
    return {
      totalReferrals: 0,
      referralCode: null,
      referrals: [],
    };
  }
};

/**
 * Get referral link for a user
 * @param {string} address - Wallet address
 * @returns {Promise<string>} Full referral URL
 */
export const getReferralLink = async (address) => {
  try {
    const code = await getOrCreateReferralCode(address);
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${code}`;
  } catch (error) {
    console.error("Error getting referral link:", error);
    throw error;
  }
};
